import {
  SPI,
  ViewWidget,
  DslNodeWidget,
  useMatched,
  Widget,
  Subscription,
  subscribeRoute,
  http,
  useMessageHub,
  MenuService,
  MenuUrlParameters,
  MultiTabsConfigManager,
  RuntimeMenu,
  RuntimeViewAction,
  ViewActionTarget,
  useRouter,
  Router,
  MessageHub
} from '@kunlun/dependencies';
import Comp from './Notice.vue';
import { MessageHubKey } from '@/constants/messageHub';
import { OioNotification } from '@kunlun/vue-ui-antd';

export interface NoticeItem {
  contentTemplate: string;
  conditionMap: Record<string, any>;
  viewAction: RuntimeViewAction;
}

export interface ChatMessage {
  id?: number;
  senderType: string;
  messageContent: string;
  messageType: string;
  sendTime?: string;
}

// 智能客服对话 GQL 入口映射：后端为每个工作台建了独立代理 Model，
// 不同工作台使用独立 mutation 名称，避免多 Action 共用同一入口冲突
const CHAT_MUTATION_MAP: Record<string, string> = {
  // 分销运营工作台 -> DeliAriesPlatformChatAction
  deliAriesCorePlatform: 'deliAriesPlatformChatInteractionMutation',
  // 品牌分销工作台 -> DeliAriesSellerChatAction
  deliAriesCoreSeller: 'deliAriesSellerChatInteractionMutation'
};

@SPI.ClassFactory(
  ViewWidget.Token({
    widget: 'NoticeWidget'
  })
)
export default class NoticeWidget extends DslNodeWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(Comp);
    return this;
  }

  private routeSubscription!: Subscription;
  protected router!: Router;

  @Widget.Reactive()
  noticeList: NoticeItem[] = [];

  @Widget.Reactive()
  total = 0;

  @Widget.Reactive()
  moduleStr = '';
  interval: any;

  includeModels = ['deliAriesCoreSeller', 'deliAriesCorePlatform'];

  @Widget.Reactive()
  get show() {
    return this.includeModels.includes(this.moduleStr);
  }

  // === 智能客服聊天状态 ===
  @Widget.Reactive()
  chatVisible: boolean = false;

  @Widget.Reactive()
  chatMessages: ChatMessage[] = [];

  @Widget.Reactive()
  chatLoading: boolean = false;

  @Widget.Reactive()
  chatSending: boolean = false;

  @Widget.Reactive()
  humanServiceUrl: string = '';

  private chatSessionId: number | null = null;

  protected async mounted() {
    this.routeSubscription = subscribeRoute(
      (url) => {
        const { matched } = useMatched();
        const { segmentParams } = matched;

        this.moduleStr = segmentParams?.page?.module;
      },
      {
        distinct: true
      }
    );

    MessageHub.on('INFO', (e) => {
      if (e === MessageHubKey.ChangeUser && this.show) {
        this.restartInterval();
      }
    });
  }

  @Widget.Method()
  async jump(item) {
    const viewAction = item.viewAction as RuntimeViewAction;
    const menus = (await MenuService.queryMenus(viewAction.moduleName)) as RuntimeMenu[];
    const treeNodes = MenuService.convert(menus);
    MenuService.sort(treeNodes);

    const selectedMenuItem = MenuService.findSelectedMenuItemByAction(treeNodes, viewAction.name);
    if (!selectedMenuItem) {
      OioNotification.error('错误', '未找到对应菜单项');
      return;
    }
    const { selectedKeys, openKeys } = MenuService.generatorMenuUrlParameters(selectedMenuItem);
    const { model, name, target } = viewAction;
    const viewType = viewAction.resView?.type || viewAction.viewType;
    const {
      page: { language = '' }
    } = useMatched().matched.segmentParams;
    const menuUrlParameters: MenuUrlParameters = {
      selectedKeys: selectedKeys || [],
      openKeys: openKeys || []
    };
    const moduleName = viewAction.resModuleName || viewAction.moduleName;
    const parameters: Record<string, string | undefined> = {
      module: moduleName,
      viewType,
      model,
      action: name,
      scene: name,
      target,
      menu: JSON.stringify(menuUrlParameters),
      expand: 'true'
    };
    if (item.conditionMap) {
      parameters.searchBody = JSON.stringify(item.conditionMap);
    }
    if (MultiTabsConfigManager.isEnabled(moduleName)) {
      parameters.target = ViewActionTarget.OpenWindow;
    } else {
      parameters.random = Date.now().toString();
    }
    if (language) {
      parameters.language = language;
    }
    this.router.push({
      segments: [
        {
          path: 'page',
          extra: { preserveParameter: false },
          parameters
        }
      ]
    });
  }

  restartInterval() {
    clearInterval(this.interval);
    this.queryNotice();
    this.interval = setInterval(() => {
      this.queryNotice();
    }, 60 * 1000);
  }

  @Widget.Watch('moduleStr', { immediate: true })
  protected async moduleStrChanged(val) {
    if (val) {
      if (this.show) {
        this.restartInterval();
      } else {
        clearInterval(this.interval);
      }
    }
  }

  async queryNotice() {
    const prefixMap = {
      // 分销运营工作台
      deliAriesCorePlatform: 'platformPartnerNoticeQuery',
      // 品牌分销工作台
      deliAriesCoreSeller: 'sellerPartnerNoticeQuery'
    };

    if (!prefixMap[this.moduleStr]) return;

    const gql = `
      query {
        ${prefixMap[this.moduleStr]} {
          queryList {
            content {
              contentTemplate
              conditionMap
              viewAction {
                id
                name
                displayName
                actionType
                target
                viewType
                contextType
                model
                resModel
                moduleName
                resModule
                resModuleName
                priority
              }
            }
            total
          }
        }
      }
    `;
    const res = await http.query<{ content: NoticeItem[]; total: number }>(this.moduleStr, gql);
    const list = res.data[prefixMap[this.moduleStr]].queryList.content;
    list.forEach((i) => (i.contentTemplate = '您好，您有' + i.contentTemplate.replace(/(\d+)/g, '<span style="color:red">$1</span>')));
    this.noticeList = list;
    this.total = res.data[prefixMap[this.moduleStr]].queryList.total;
  }

  // === 智能客服聊天方法 ===

  @Widget.Method()
  openSmartChat() {
    this.chatVisible = true;
    if (!this.chatSessionId) {
      this.startChatSession();
    }
  }

  @Widget.Method()
  closeChat() {
    this.chatVisible = false;
  }

  @Widget.Method()
  async sendChatMessage(text: string) {
    if (!text.trim() || !this.chatSessionId) return;

    // 立即显示用户消息
    const userMsg: ChatMessage = {
      senderType: 'CUSTOMER',
      messageContent: text,
      messageType: 'TEXT',
      sendTime: new Date().toISOString()
    };
    this.chatMessages = [...this.chatMessages, userMsg];

    this.chatSending = true;
    try {
      const escapedText = text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
      const mutationName = CHAT_MUTATION_MAP[this.moduleStr];
      if (!mutationName) return;
      const gql = `mutation { ${mutationName} { sendMessage(data: { sessionId: ${this.chatSessionId}, userMessage: "${escapedText}" }) { replyMessage replyMessageType status customerServiceUrl } } }`;
      const res = await http.query<any>(this.moduleStr, gql);
      const result = res?.data?.[mutationName]?.sendMessage;
      if (result?.replyMessage) {
        const replyMsg: ChatMessage = {
          senderType: result.replyMessageType === 'SYSTEM' || result.replyMessageType === 'TRANSFER_PROMPT' ? 'SYSTEM' : 'AI',
          messageContent: result.replyMessage,
          messageType: result.replyMessageType,
          sendTime: new Date().toISOString()
        };
        this.chatMessages = [...this.chatMessages, replyMsg];
        if (result.customerServiceUrl) {
          this.humanServiceUrl = result.customerServiceUrl;
        }
      }
    } catch (e) {
      console.error('Failed to send message', e);
    } finally {
      this.chatSending = false;
    }
  }

  @Widget.Method()
  openHumanService() {
    if (this.humanServiceUrl) {
      window.open(this.humanServiceUrl, '_blank');
    }
  }

  private async startChatSession() {
    this.chatLoading = true;
    try {
      const mutationName = CHAT_MUTATION_MAP[this.moduleStr];
      if (!mutationName) return;
      const gql = `mutation { ${mutationName} { startChat(data: {}) { sessionId sessionCode status humanServiceUrl historyMessages { id senderType messageContent messageType sendTime } } } }`;
      const res = await http.query<any>(this.moduleStr, gql);
      const result = res?.data?.[mutationName]?.startChat;
      if (result) {
        this.chatSessionId = result.sessionId;
        this.humanServiceUrl = result.humanServiceUrl || '';
        this.chatMessages = result.historyMessages || [];
      }
    } catch (e) {
      console.error('Failed to start chat', e);
    } finally {
      this.chatLoading = false;
    }
  }

  protected $$mounted() {
    super.$$mounted();
    this.router = useRouter().router;
  }
}
